export interface FormatSecondsToCompactDurationOptions {
  /**
   * Pads the leading unit to two digits (`05:03` instead of `5:03`).
   * Defaults to `false`.
   */
  padLeadingUnit?: boolean;
}

/**
 * Format seconds to a compact duration, omitting the hours component when the
 * duration is under an hour.
 *
 * Non-finite and negative inputs are clamped to `0` and fractional seconds are
 * floored, so this never renders values like `-1:-5`, `62:00` or `1:3.5`.
 *
 * @example
 * formatSecondsToCompactDuration(5) -> "0:05"
 * formatSecondsToCompactDuration(65) -> "1:05"
 * formatSecondsToCompactDuration(3725) -> "1:02:05"
 * formatSecondsToCompactDuration(-5) -> "0:00"
 * formatSecondsToCompactDuration(5, { padLeadingUnit: true }) -> "00:05"
 */
export const formatSecondsToCompactDuration = (
  seconds: number,
  options: FormatSecondsToCompactDurationOptions = {}
): string => {
  const { padLeadingUnit = false } = options;

  const totalSeconds = Number.isFinite(seconds)
    ? Math.max(0, Math.floor(seconds))
    : 0;

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const remainingSeconds = totalSeconds % 60;

  const leadingUnit = hours > 0 ? hours : minutes;
  const leadingUnitLabel = padLeadingUnit
    ? String(leadingUnit).padStart(2, "0")
    : String(leadingUnit);

  const parts =
    hours > 0
      ? [leadingUnitLabel, String(minutes).padStart(2, "0")]
      : [leadingUnitLabel];

  return [...parts, String(remainingSeconds).padStart(2, "0")].join(":");
};
